import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import pageStyles from '../styles/page-common.module.css';
import { getUserCounts, getPrompts, getConstant, getTreeMetadata } from '../backend';
import { getWeekIndex, getStartDateOfWeek } from '../utils';
import { LEVEL_NAMES, LANGUAGE_NAMES } from '../constants';
import Loading from '../components/Loading';


// section showing user counts, broken down by subscription type
function LLUserSection({username}) {

  // react states
  const [totalUsers, setTotalUsers] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // on mount, load user counts
  useEffect(() => {
    async function fetchUserCount() {
      try {
          const response = await getUserCounts(username)
          setTotalUsers(response.total_users);
      } catch (err) {
          setError('Failed to load data');
      } finally {
          setLoading(false);
      }
    };
    fetchUserCount();
  }, [username]);

  // if still loading or download error, display loading/error respectively
  if (loading) {
    return <p>Loading...</p>
  } else if (error) {
    return <div className={pageStyles.alertBanner}><p>{error}</p></div>
  }


  return (
    <>
      <h2>LingoLeaf Users</h2>
      <p><strong>Total User Count:</strong> {totalUsers}</p>
    </>
  )
}

// section showing start dates and week indices of next few weeks
function LLCalendar() {

  const currentWeekIndex = getWeekIndex();

  const weeks = Array.from({ length: 6 }, (_, i) => {
    const weekIndex = currentWeekIndex + i;
    const startDate = getStartDateOfWeek(weekIndex);
    return {
      weekIndex,
      startDate: startDate.toDateString(),
    };
  });

  return (
    <>
      <h2>LingoLeaf Calendar</h2>
      <p>This week is week {currentWeekIndex}. Start dates of the next few weeks:</p>
      <ul>
        {weeks.map(({ weekIndex, startDate }) => (
          <li key={weekIndex}>
            <strong>Week {weekIndex}:</strong> {startDate}
          </li>
        ))}
      </ul>
    </>
  )
}

// component: table to show prompts
function PromptTable({username, languages, viewWeek}) {

  // react states
  const [promptStatusMap, setPromptStatusMap] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // on mount, load prompts
  useEffect(() => {
    const ids = languages.flatMap(lang =>
      Object.keys(LEVEL_NAMES).map(level => `${lang}_${viewWeek}_${level}`)
    )
    const fetchPrompts = async () => {
      try {
        setError('');
        setLoading(true);   
        const response = await getPrompts(username, ids)
        const map = ids.reduce((acc, id) => {
          acc[id] = response.some(prompt => prompt._id === id) ? '✅' : '❌';
          return acc;
        }, {});
        setPromptStatusMap(map);
      } catch (err) {
        if (err.response.status === 404) {
          const map = ids.reduce((acc, id) => {
            acc[id] = '❌'
            return acc
          }, {});
          setPromptStatusMap(map);
        }
        else {
          setError(`Failed to load data: ${err.response.status}`);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchPrompts();
  }, [username, languages, viewWeek]);

  // if still loading or download error, display loading/error respectively
  if (loading) {
    return <p>Loading...</p>
  } else if (error) {
    return <div className={pageStyles.alertBanner}><p>{error}</p></div>
  }

  return (
    <table style={{ width: '40%', borderCollapse: 'collapse' }}>
      <thead>
          <tr style={{ borderBottom: '2px solid #ccc' }}>
              <th style={{ padding: '0.5rem', borderRight: '2px solid #ccc' }}>Language</th>
              {Object.keys(LEVEL_NAMES).map(level => <th key={level} style={{ padding: '0.5rem' }}>{LEVEL_NAMES[level]}</th>)}
          </tr>
      </thead>
      <tbody>
          {languages.map(lang => (
              <tr key={lang}>
                  <td style={{ padding: '0.25rem', borderRight: '2px solid #ccc' }} >{LANGUAGE_NAMES[lang]}</td>
                  {Object.keys(LEVEL_NAMES).map(level => (
                      <td key={`${lang}_${level}`}>
                          {promptStatusMap[`${lang}_${viewWeek}_${level}`] || '❓'}
                      </td>
                  ))}
              </tr>
          ))}
      </tbody>
    </table>
  )
}


// component: table to show trees
function TreeTable({username, languages, viewWeek}) {

  // react states
  const [treeStatusMap, setTreeStatusMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // on mount, load tree metedata
  useEffect(() => {
    const ids = languages.flatMap(lang =>
      Object.keys(LEVEL_NAMES).map(level => `${lang}_${viewWeek}_${level}`)
    );
    const fetchTrees = async () => {
      try {
          setError('');
          setLoading(true);
          const response = await getTreeMetadata({ids})
          const map = ids.reduce((acc, id) => {
            acc[id] = response.some(prompt => prompt._id === id) ? '✅' : '❌';
            return acc;
          }, {});
          setTreeStatusMap(map);
      } catch (err) {
          if (err.response.status === 404) {
            const map = ids.reduce((acc, id) => {
              acc[id] = '❌'
              return acc
            }, {});
            setTreeStatusMap(map);
          }
          else {
            setError(`Failed to load data: ${err.response.status}`);
          }
      } finally {
          setLoading(false);
      }
    };
    fetchTrees();  
  }, [username, languages, viewWeek]);

  // if still loading or download error, display loading/error respectively
  if (loading) {
    return <p>Loading...</p>
  } else if (error) {
    return <div className={pageStyles.alertBanner}><p>{error}</p></div>
  }

  return (
    <table style={{ width: '40%', borderCollapse: 'collapse' }}>
      <thead>
          <tr style={{ borderBottom: '2px solid #ccc' }}>
              <th style={{ padding: '0.5rem', borderRight: '2px solid #ccc' }}>Language</th>
              {Object.keys(LEVEL_NAMES).map(level => <th key={level} style={{ padding: '0.5rem' }}>{LEVEL_NAMES[level]}</th>)}
          </tr>
      </thead>
      <tbody>
          {languages.map(lang => (
              <tr key={lang}>
                  <td style={{ padding: '0.25rem', borderRight: '2px solid #ccc' }} >{LANGUAGE_NAMES[lang]}</td>
                  {Object.keys(LEVEL_NAMES).map(level => (
                      <td key={`${lang}_${level}`}>
                          {treeStatusMap[`${lang}_${viewWeek}_${level}`] || '❓'}
                      </td>
                  ))}
              </tr>
          ))}
      </tbody>
    </table>
  )
}


// main component: Staff Dashboard page
function StaffDashboard() {
  
  // get username from redux state
  const username = useSelector((state) => state.user).username;

  // react states
  const [viewWeek, setViewWeek] = useState(getWeekIndex() + 1); // which week to see prompts/trees
  const [languages, setLanguages] = useState([]);               // list of LL languages
  const [loading, setLoading] = useState(true);                 // languages loading
  const [error, setError] = useState('');                       // error downloading languages

  // on mount, load list of LL languages
  useEffect(() => {
    async function loadLanguages() {
      try {
        setError('');
        setLoading(true);
        const response = await getConstant({name: 'reading_languages'});
        setLanguages(response);
      } catch (err) {
        setError('Failed to load languages');
      } finally {
        setLoading(false);
      }
    }
    loadLanguages();
  }, []);

  // if still loading or download error, display loading/error respectively
  if (loading) {
    return <Loading/>
  } else if (error) {
    return (
      <div className={pageStyles.styledPage}>
        <h1>Staff Dashboard</h1>
        <div className={pageStyles.alertBanner}><p>{error}</p></div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Staff Dashboard | LingoLeaf</title>
      </Helmet>
      <div className={pageStyles.styledPage}>
        <h1>Staff Dashboard</h1>
        <LLUserSection username={username}/>
        <hr className={pageStyles.hrule} />
        <LLCalendar/>
        <hr className={pageStyles.hrule} />
        <h2>Prompts & Trees</h2>
        <label htmlFor="number-input" className="block text-lg font-bold">This week is week {getWeekIndex()}. Query Database for week:</label>
        <input
            id="number-input"
            type="number"
            value={viewWeek}
            onChange={(e) => setViewWeek(parseInt(e.target.value, 10) || 0)}
        />
        <h3>Prompts</h3>
        <PromptTable languages={languages} username={username} viewWeek={viewWeek}/>
        <h3>Trees</h3>
        <TreeTable languages={languages} username={username} viewWeek={viewWeek}/>
      </div>
    </>
  );
}


export default StaffDashboard;